import { useEffect, useRef, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  ChevronDown,
  ChevronLeft,
  Crown,
  Folder,
  Home,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  Users,
  Video,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"
import StatusBadge from "@/components/tuw/StatusBadge"
import MessageThread from "@/components/tuw/chat/MessageThread"
import ChatComposer from "@/components/tuw/chat/ChatComposer"
import { Button } from "@/components/ui/button"
import {
  CreateChannelModal,
  CreateTeamModal,
  InviteModal,
  RemoveMemberModal,
  RenameChannelModal,
  RenameTeamModal,
  ShareOrderModal,
} from "@/components/tuw/team/TeamModals"
import { GatedState, NoTeamsHero } from "@/components/tuw/team/EmptyStates"
import useMediaQuery from "@/hooks/useMediaQuery"
import useOptimisticSend from "@/hooks/useOptimisticSend"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"
import { launchVideoCall } from "@/lib/videoCall"

// Navigate to a team / conversation via the refresh-safe params (?team, ?c), so
// a deep-link and a reload both land in the same place (server re-renders the
// active conversation + thread).
function visitTeam(teamId, conversationId) {
  const data = { team: teamId }
  if (conversationId) data.c = conversationId
  router.get("/team", data, { preserveScroll: true })
}

// Phone crumb-bar titles for the management pages (S-TEAM-WORKSPACE T8).
const PAGE_TITLES = { overview: "Overview", members: "Members", orders: "Shared projects" }

// ---- team switcher --------------------------------------------------------

function TeamSwitcher({ teams, activeTeam, onCreate }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  return (
    <div ref={ref} className="relative border-b border-border p-[13px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-[11px] text-left"
      >
        <span className="grid size-[34px] shrink-0 place-items-center rounded-[10px] bg-foreground text-background">
          <Users className="size-[17px]" strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1">
          <b className="block truncate font-display text-[14.5px] font-bold tracking-[-0.2px] leading-tight">
            {activeTeam.name}
          </b>
          <span className="text-[11px] text-ink-3">
            {activeTeam.memberCount} member{activeTeam.memberCount === 1 ? "" : "s"}
          </span>
        </span>
        <ChevronDown className={cn("size-4 shrink-0 text-ink-3 transition-transform", open && "rotate-180")} strokeWidth={2.2} />
      </button>

      {open && (
        <div className="absolute inset-x-2.5 top-[calc(100%-2px)] z-60 rounded-[13px] border border-line-2 bg-card p-1.5 shadow-[0_22px_50px_-16px_rgba(0,0,0,0.3)]">
          {teams.map((team) => (
            <button
              key={team.id}
              type="button"
              onClick={() => { setOpen(false); if (team.id !== activeTeam.id) visitTeam(team.id) }}
              className="flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left hover:bg-accent"
            >
              <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-secondary font-display text-[12px] font-bold text-muted-foreground">
                {team.name[0]?.toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <b className="block truncate text-[12.5px] font-semibold">{team.name}</b>
                <span className="text-[10.5px] text-ink-3">{team.memberCount} members</span>
              </span>
              {team.unread > 0 && (
                <span className="rounded-full bg-primary px-1.5 text-[9px] font-bold text-primary-foreground">{team.unread}</span>
              )}
            </button>
          ))}
          <div className="mt-1.5 border-t border-border pt-1.5">
            <button
              type="button"
              onClick={() => { setOpen(false); onCreate() }}
              className="flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left text-[12.5px] font-semibold text-muted-foreground hover:bg-accent"
            >
              <Plus className="size-4 text-ink-3" strokeWidth={2} />
              Create a new team
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- workspace rail -------------------------------------------------------

function RailPageItem({ Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex w-full items-center gap-[9px] rounded-lg px-[9px] py-[7px] text-[13px] font-medium transition-colors",
        active ? "bg-[var(--red-soft)] font-semibold text-foreground" : "text-muted-foreground hover:bg-accent"
      )}
    >
      {active && <span className="absolute top-[7px] bottom-[7px] left-0 w-[3px] rounded-[3px] bg-primary" />}
      <Icon className={cn("size-4 shrink-0", active ? "text-primary" : "text-ink-3")} strokeWidth={1.9} />
      <span className="truncate">{label}</span>
    </button>
  )
}

// A named-channel row. Owners get a ••• menu with Rename (order threads never do
// — the channel/thread invariant). The row is a div (not a button) so the menu
// trigger can nest without invalid button-in-button markup.
function RailChannelItem({ name, unread, active, canManage, onClick, onRename }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!menuOpen) return undefined
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false) }
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [menuOpen])

  return (
    <div
      ref={ref}
      className={cn(
        "group relative flex w-full items-center gap-[9px] rounded-lg px-[9px] py-[7px] text-[13px] font-medium transition-colors",
        active ? "bg-[var(--red-soft)] font-semibold text-foreground" : "text-muted-foreground hover:bg-accent"
      )}
    >
      {active && <span className="absolute top-[7px] bottom-[7px] left-0 w-[3px] rounded-[3px] bg-primary" />}
      <button type="button" onClick={onClick} className="flex min-w-0 flex-1 items-center gap-[9px] text-left">
        <span className={cn("w-[17px] text-center text-[15px] font-semibold", active ? "text-primary" : "text-ink-3")}>#</span>
        <span className="min-w-0 flex-1 truncate">{name}</span>
      </button>
      {unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[9.5px] font-bold text-primary-foreground">{unread}</span>}
      {canManage && (
        <button
          type="button"
          title="Channel options"
          onClick={() => setMenuOpen((v) => !v)}
          className="grid size-5 shrink-0 place-items-center rounded text-ink-3 opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"
        >
          <MoreHorizontal className="size-[15px]" strokeWidth={2} />
        </button>
      )}
      {menuOpen && (
        <div className="absolute top-[calc(100%-2px)] right-1.5 z-60 w-[150px] rounded-[10px] border border-line-2 bg-card p-1 shadow-[0_18px_44px_-14px_rgba(0,0,0,0.3)]">
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onRename() }}
            className="flex w-full items-center gap-2 rounded-[7px] px-2.5 py-1.5 text-left text-[12.5px] font-medium text-muted-foreground hover:bg-accent"
          >
            <Pencil className="size-[14px] text-ink-3" strokeWidth={2} />
            Rename channel
          </button>
        </div>
      )}
    </div>
  )
}

// An order-thread rail row. Lists a shared order; if its thread already exists it
// carries a conversationId and opens directly, otherwise the click POSTs to
// lazily create it. A status chip mirrors the order header. Never renameable.
function RailOrderThreadItem({ thread, active, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(thread)}
      className={cn(
        "relative flex w-full items-center gap-[9px] rounded-lg px-[9px] py-[7px] text-[13px] font-medium transition-colors",
        active ? "bg-[var(--red-soft)] font-semibold text-foreground" : "text-muted-foreground hover:bg-accent"
      )}
    >
      {active && <span className="absolute top-[7px] bottom-[7px] left-0 w-[3px] rounded-[3px] bg-primary" />}
      <Folder className={cn("size-4 shrink-0", active ? "text-primary" : "text-ink-3")} strokeWidth={1.9} />
      <span className="min-w-0 flex-1 truncate">{thread.title}</span>
      {thread.status && (
        <StatusBadge tier={thread.status.tier} label={thread.status.label} className="shrink-0 px-1.5 py-0.5 text-[9px]" />
      )}
      {thread.unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[9.5px] font-bold text-primary-foreground">{thread.unread}</span>}
    </button>
  )
}

function WorkspaceRail({ team, activePage, activeConversationId, onSelectPage, onOpenDm, onOpenThread, onCreateChannel, onRenameChannel, onInvite }) {
  const rail = team.rail ?? { channels: [], threads: [], dms: [] }
  const threads = rail.threads ?? []
  const showingConversation = activePage === null
  const canManage = team.activeTeam.isOwner

  return (
    <div className="flex w-[236px] shrink-0 flex-col border-r border-border bg-card max-[721px]:w-full max-[721px]:border-r-0">
      <TeamSwitcher teams={team.teams} activeTeam={team.activeTeam} onCreate={onInvite.createTeam} />

      <div className="flex-1 overflow-y-auto px-2 pt-2.5 pb-3.5">
        <div className="mt-0.5">
          <RailPageItem Icon={Home} label="Overview" active={activePage === "overview"} onClick={() => onSelectPage("overview")} />
          <RailPageItem Icon={Users} label="Members" active={activePage === "members"} onClick={() => onSelectPage("members")} />
          <RailPageItem Icon={Folder} label="Shared projects" active={activePage === "orders"} onClick={() => onSelectPage("orders")} />
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[10px] font-bold tracking-[0.07em] text-ink-3 uppercase">Channels</span>
            {canManage && (
              <button
                type="button"
                title="Add channel"
                onClick={onCreateChannel}
                className="grid size-[18px] place-items-center rounded text-ink-3 hover:bg-accent hover:text-foreground"
              >
                <Plus className="size-[14px]" strokeWidth={2.2} />
              </button>
            )}
          </div>
          {rail.channels.map((channel) => (
            <RailChannelItem
              key={channel.id}
              name={channel.name}
              unread={channel.unread}
              active={showingConversation && activeConversationId === channel.id}
              canManage={canManage}
              onClick={() => visitTeam(team.activeTeam.id, channel.id)}
              onRename={() => onRenameChannel(channel)}
            />
          ))}
        </div>

        {threads.length > 0 && (
          <div className="mt-3">
            <div className="px-2 py-1 text-[10px] font-bold tracking-[0.07em] text-ink-3 uppercase">Order threads</div>
            {threads.map((thread) => (
              <RailOrderThreadItem
                key={thread.orderId}
                thread={thread}
                active={showingConversation && !!thread.conversationId && activeConversationId === thread.conversationId}
                onOpen={onOpenThread}
              />
            ))}
          </div>
        )}

        <div className="mt-3">
          <div className="px-2 py-1 text-[10px] font-bold tracking-[0.07em] text-ink-3 uppercase">Direct messages</div>
          {rail.dms.length === 0 ? (
            <div className="px-2.5 py-1.5 text-[11.5px] text-ink-3">Invite teammates to start a DM.</div>
          ) : (
            rail.dms.map((dm) => (
              <button
                key={dm.memberUserId}
                type="button"
                onClick={() => onOpenDm(dm)}
                className={cn(
                  "relative flex w-full items-center gap-[9px] rounded-lg px-[9px] py-[7px] text-[13px] font-medium transition-colors",
                  showingConversation && dm.conversationId && activeConversationId === dm.conversationId
                    ? "bg-[var(--red-soft)] font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-accent"
                )}
              >
                <span className="size-[7px] shrink-0 rounded-full bg-line-2" />
                <span className="min-w-0 flex-1 truncate">{dm.name}</span>
                {dm.unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[9.5px] font-bold text-primary-foreground">{dm.unread}</span>}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="border-t border-border p-2.5">
        <Button variant="outline" className="w-full justify-center gap-1.5 font-semibold" onClick={onInvite.invite}>
          <UserPlus className="size-4" strokeWidth={2} />
          Invite freelancer
        </Button>
      </div>
    </div>
  )
}

// ---- conversation view ----------------------------------------------------

function ConversationView({
  conversation,
  memberCount,
  onSend,
  sending,
  empty,
  onInvite,
  onShare,
  onBack,
  onMembers,
  pendingItems = [],
  onRetry,
  onEdit,
  onDismiss,
  composerRef,
}) {
  if (!conversation) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-accent px-10 text-center">
        <div className="mb-[18px] grid size-[58px] place-items-center rounded-2xl border border-border bg-card">
          <MessageSquare className="size-[26px] text-ink-3" strokeWidth={1.6} />
        </div>
        <h3 className="font-display text-base font-bold tracking-[-0.2px]">Select a channel</h3>
        <p className="mt-1.5 max-w-[340px] text-[13px] leading-[1.55] text-ink-3">
          Pick a channel or a direct message on the left to read and reply.
        </p>
      </div>
    )
  }

  const isChannel = conversation.kind === "channel"

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-card">
      <div className="flex min-h-[56px] shrink-0 items-center gap-3 border-b border-border px-[18px] py-[13px]">
        {/* Phone-only back control (S-TEAM-WORKSPACE T7): the slide-over
            returns to the channel rail without navigation. */}
        <button
          type="button"
          aria-label="Back to channels"
          onClick={onBack}
          className="-ml-1.5 hidden size-9 shrink-0 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground max-[721px]:flex"
        >
          <ChevronLeft className="size-5" strokeWidth={2.2} />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          {isChannel && <span className="text-[17px] font-semibold text-ink-3">#</span>}
          <h2 className="truncate font-display text-base font-bold tracking-[-0.2px]">{conversation.name}</h2>
          {/* The phone header sheds its sub-line (T7); the design's pin/online
              affordances have no live backing and are not built. */}
          <span className="whitespace-nowrap border-l border-line-2 pl-2.5 text-[11.5px] text-ink-3 max-[721px]:hidden">
            {isChannel ? `${memberCount} member${memberCount === 1 ? "" : "s"}` : "Direct message"}
          </span>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/* Order threads only (AU-37): a thread opened on a shared project is
              the one conversation with somewhere to go back to, and the payload
              carries the id only when this customer can actually open it. A
              real Inertia Link, not a button — it is a navigation, and it
              should middle-click and open in a new tab like one. */}
          {conversation.orderId && (
            <Link
              href={`/projects/${conversation.orderId}`}
              // The label collapses to the icon on a phone, so the accessible
              // name is set explicitly rather than left to the visible text.
              aria-label="Open project"
              title="Open project"
              className="flex h-[34px] shrink-0 items-center gap-1.5 rounded-[10px] border border-border bg-card px-3 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <Folder className="size-4" strokeWidth={1.9} />
              <span className="max-[721px]:hidden">Open project</span>
            </Link>
          )}
          {/* One-to-one only. A team DM has exactly one other person to call and
              the call surface renders a single remote tile; channels get no
              control at all, because group calling is declined, not deferred,
              and the resolver correctly refuses a partner-less launch (AT-15).
              Raw button per Hallmark R7. */}
          {!isChannel && conversation.partnerUserId && (
            <button
              type="button"
              title="Video call"
              onClick={() =>
                launchVideoCall(conversation.partnerUserId, { conversationSid: conversation.sid })
              }
              className="grid size-[34px] place-items-center rounded-[10px] border border-border bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <Video className="size-[17px]" strokeWidth={1.9} />
            </button>
          )}
          {/* Members affordance (T5): while the members rail is hidden (≤980)
              this real-data control — existing member count, no invented
              presence — is the Members-page entry. Raw button per Hallmark R7. */}
          <button
            type="button"
            aria-label={`Team members (${memberCount})`}
            onClick={onMembers}
            className="hidden h-[34px] shrink-0 items-center gap-1.5 rounded-[10px] border border-border bg-card px-3 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground max-[981px]:flex"
          >
            <Users className="size-4" strokeWidth={1.9} />
            {memberCount}
          </button>
        </div>
      </div>

      <MessageThread
        items={[...(conversation.thread ?? []), ...pendingItems]}
        onRetry={onRetry}
        onEdit={onEdit}
        onDismiss={onDismiss}
        empty={
          empty ? (
            <div className="m-auto max-w-[420px] px-4 text-center">
              <div className="mx-auto mb-3 grid size-[46px] place-items-center rounded-[13px] bg-secondary text-[24px] font-semibold text-ink-3">#</div>
              <h3 className="font-display text-lg font-bold tracking-[-0.3px]">#{conversation.name}</h3>
              <p className="mx-auto mt-1.5 max-w-[42ch] text-[12.5px] leading-[1.5] text-ink-3">
                Invite freelancers you&apos;ve worked with and share a project to get the conversation going.
              </p>
              <div className="mt-4 flex justify-center gap-2.5">
                <Button size="sm" onClick={onInvite}>
                  <UserPlus className="size-4" strokeWidth={2} /> Invite freelancers
                </Button>
                <Button size="sm" variant="outline" onClick={onShare}>Share a project</Button>
              </div>
            </div>
          ) : (
            <div className="m-auto max-w-[280px] text-center">
              <MessageSquare className="mx-auto size-7 text-ink-3" strokeWidth={1.6} />
              <p className="mt-3 text-[12.5px] leading-[1.55] text-muted-foreground">
                {conversation.topic || "No messages yet. Say hello."}
              </p>
            </div>
          )
        }
      />

      <ChatComposer
        ref={composerRef}
        placeholder={isChannel ? `Message #${conversation.name}…` : `Message ${conversation.name}…`}
        onSend={onSend}
        sending={sending}
      />
    </div>
  )
}

// ---- members rail ---------------------------------------------------------

function MembersRail({ members }) {
  return (
    // Hidden ≤980 (design .twright rule); the header Members affordance takes
    // over as the Members entry there (S-TEAM-WORKSPACE T5).
    <div className="flex w-[232px] shrink-0 flex-col border-l border-border bg-card max-[981px]:hidden">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2 text-[11px] font-bold tracking-[0.06em] text-ink-3 uppercase">
        <span>Members</span>
        <span className="font-semibold">{members.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto px-2.5 pb-3.5">
        {members.map((member) => (
          <div key={member.userId ?? member.name} className="flex items-center gap-2.5 rounded-[9px] px-2 py-[7px]">
            <Avatar initials={member.initials} name={member.name} size={30} me={member.owner} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
                <span className="truncate">{member.owner ? "You" : member.name}</span>
                {member.owner && <Crown className="size-3 text-[var(--amber)]" strokeWidth={2} fill="none" />}
              </div>
              <div className="text-[10.5px] text-ink-3">{member.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- management pages -----------------------------------------------------

function OverviewPage({ team, onRename, onDelete, onMembers, onOrders }) {
  const active = team.activeTeam
  const channelCount = (team.rail?.channels ?? []).length
  const overview = active.overview ?? { members: [], projects: [], activity: [] }
  const stats = [
    { label: "Members", value: active.memberCount },
    { label: "Shared projects", value: active.sharedOrders.length },
    { label: "Channels", value: channelCount },
  ]

  return (
    <div className="flex-1 overflow-y-auto bg-accent px-6 py-6">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h1 className="font-display text-2xl font-bold tracking-[-0.4px]">{active.name}</h1>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="outline" onClick={onRename}>Rename</Button>
            <Button size="sm" variant="outline" className="text-primary hover:text-primary" onClick={onDelete}>
              Delete team
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-[13px] border border-border bg-card px-4 py-3.5">
              <div className="text-[11px] font-semibold text-ink-3">{stat.label}</div>
              <div className="mt-1.5 font-display text-2xl font-bold tracking-[-0.5px]">{stat.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <section className="rounded-[13px] border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="font-display text-[14px] font-bold">Members</h2>
              <Button size="sm" variant="ghost" onClick={onMembers}>View all</Button>
            </div>
            <div className="flex flex-col gap-2">
              {overview.members.map((member) => (
                <div key={member.userId ?? member.name} className="flex items-center gap-2.5 rounded-[9px] bg-secondary/45 px-2.5 py-2">
                  <Avatar initials={member.initials} name={member.name} size={30} me={member.owner} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-semibold">{member.owner ? "You" : member.name}</div>
                    <div className="text-[10.5px] text-ink-3">{member.owner ? "Account owner" : member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[13px] border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="font-display text-[14px] font-bold">Shared projects</h2>
              <Button size="sm" variant="ghost" onClick={onOrders}>View all</Button>
            </div>
            {overview.projects.length === 0 ? (
              <p className="rounded-[9px] bg-secondary/45 px-3 py-5 text-center text-[12px] text-ink-3">No projects shared yet.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {overview.projects.map((project) => (
                  <Link
                    key={project.orderId}
                    href={project.href}
                    className="flex items-center gap-2.5 rounded-[9px] bg-secondary/45 px-2.5 py-2 hover:bg-secondary"
                  >
                    <Folder className="size-4 shrink-0 text-ink-3" />
                    <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">{project.title}</span>
                    <StatusBadge tier={project.status.tier} label={project.status.label} className="px-2 py-0.5 text-[9.5px]" />
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        <section className="mt-4 rounded-[13px] border border-border bg-card p-4">
          <h2 className="mb-3 font-display text-[14px] font-bold">Recent activity</h2>
          {overview.activity.length === 0 ? (
            <p className="rounded-[9px] bg-secondary/45 px-3 py-5 text-center text-[12px] text-ink-3">
              Team activity will appear here.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {overview.activity.map((activity, index) => (
                <div key={`${activity.kind}-${activity.at}-${index}`} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold">{activity.title}</div>
                    {activity.detail && <div className="mt-0.5 truncate text-[11px] text-ink-3">{activity.detail}</div>}
                  </div>
                  <time className="shrink-0 text-[10.5px] text-ink-3">{activity.at}</time>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function MembersPage({ team, onInvite, onOpenDm, onRemoveMember }) {
  const active = team.activeTeam
  return (
    <div className="flex-1 overflow-y-auto bg-accent px-6 py-6">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-[15px] font-bold tracking-[-0.2px]">Members · {active.memberCount}</h3>
          <Button size="sm" onClick={onInvite}>
            <UserPlus className="size-4" strokeWidth={2} /> Invite freelancer
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-2.5 min-[721px]:grid-cols-2">
          {active.members.map((member) => (
            <div key={member.userId ?? member.name} className="flex items-center gap-3 rounded-[13px] border border-border bg-card p-3.5">
              <Avatar initials={member.initials} name={member.name} size={40} me={member.owner} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 font-display text-[13.5px] font-semibold">
                  <span className="truncate">{member.owner ? "You" : member.name}</span>
                  {member.owner && <Crown className="size-3.5 text-[var(--amber)]" strokeWidth={2} fill="none" />}
                </div>
                <div className="text-[11.5px] text-ink-3">{member.owner ? "Account owner" : member.role}</div>
              </div>
              {!member.owner && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    title="Message"
                    onClick={() => onOpenDm({ memberUserId: member.userId })}
                    className="grid size-[30px] place-items-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <MessageSquare className="size-[15px]" strokeWidth={2} />
                  </button>
                  {member.slug && (
                    <Link
                      href={`/freelancers/${member.slug}`}
                      title="View profile"
                      className="grid size-[30px] place-items-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
                    >
                      <Users className="size-[15px]" strokeWidth={2} />
                    </Link>
                  )}
                  <button
                    type="button"
                    title="Remove from team"
                    aria-label={`Remove ${member.name} from team`}
                    onClick={() => onRemoveMember(member)}
                    className="grid size-[30px] place-items-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-secondary hover:text-primary"
                  >
                    <Trash2 className="size-[15px]" strokeWidth={2} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {active.invites.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-3 font-display text-[15px] font-bold tracking-[-0.2px]">Pending invites</h3>
            <div className="flex flex-col gap-2">
              {active.invites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-3.5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-[13px] font-semibold">{invite.name}</div>
                    {invite.note && <div className="truncate text-[11.5px] text-ink-3">{invite.note}</div>}
                  </div>
                  <StatusBadge tier="needs_you" label="Pending" className="px-2 py-0.5 text-[10px]" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SharedOrdersPage({ team, onShare, onUnshare, onOpenThread }) {
  const active = team.activeTeam
  return (
    <div className="flex-1 overflow-y-auto bg-accent px-6 py-6">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-[15px] font-bold tracking-[-0.2px]">Shared projects · {active.sharedOrders.length}</h3>
          <Button size="sm" variant="outline" onClick={onShare}>Share a project</Button>
        </div>

        {active.sharedOrders.length === 0 ? (
          <div className="rounded-[13px] border border-dashed border-border bg-card px-4 py-10 text-center text-[13px] text-ink-3">
            No projects shared yet.
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {active.sharedOrders.map((order) => (
              <div key={order.orderId} className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-3.5 py-3">
                <Link
                  href={order.href}
                  className="min-w-0 flex-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="truncate font-display text-[13px] font-semibold hover:underline">{order.title}</div>
                  <div className="text-[11px] text-ink-3">{order.type}</div>
                </Link>
                <StatusBadge tier={order.status.tier} label={order.status.label} className="px-2 py-0.5 text-[10px]" />
                <button
                  type="button"
                  title="Open thread"
                  onClick={(e) => { e.stopPropagation(); onOpenThread(order.orderId) }}
                  className="grid size-[30px] shrink-0 place-items-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  <MessageSquare className="size-[15px]" strokeWidth={2} />
                </button>
                <button
                  type="button"
                  title="Remove from team"
                  onClick={(e) => { e.stopPropagation(); onUnshare(order.orderId) }}
                  className="grid size-[30px] shrink-0 place-items-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-secondary hover:text-primary"
                >
                  <Trash2 className="size-[15px]" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ---- page -----------------------------------------------------------------

export default function Team({ team }) {
  const flags = team.flags ?? {}
  const teams = team.teams ?? []
  const activeTeam = team.activeTeam
  const activeConversation = team.activeConversation

  const [activePage, setActivePage] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [createChannelOpen, setCreateChannelOpen] = useState(false)
  const [renameChannel, setRenameChannel] = useState(null)
  // The member whose removal awaits confirmation; null when the dialog is closed.
  const [removingMember, setRemovingMember] = useState(null)
  const composerRef = useRef(null)

  // Pocket Door (S-TEAM-WORKSPACE T6-T8): at phone width the channel rail is
  // home and any selection slides its pane in. paneOpen tracks EXPLICIT intent
  // only — initialized from the `c` query param (a conversation deep link is an
  // explicit open, Temper TF-002; the presenter's first-channel fallback is
  // NOT). Rail selections re-open it: conversation rows visit /team?c=… (the
  // remount re-initializes true) and management pages go through selectPage.
  // Back controls close it locally — the URL keeps ?c, so a reload re-opens
  // the deep-linked pane by design.
  const isPhone = useMediaQuery("(max-width: 720px)")
  const [paneOpen, setPaneOpen] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).has("c")
  )
  const paneVisible = !isPhone || paneOpen

  const selectPage = (page) => {
    setActivePage(page)
    setPaneOpen(true)
  }

  // Optimistic send for the active channel / DM: the bubble renders immediately,
  // reconciles on the team reload, and shows a per-bubble error with retry on
  // failure. flagged "Edit" pulls the rejected text back into the composer.
  const { pendingItems, send, retry, editAndRemove, dismiss, sending } = useOptimisticSend({
    sid: activeConversation?.sid,
    only: ["team"],
    serverItems: activeConversation?.thread ?? [],
  })

  const handleEdit = (clientId) => {
    composerRef.current?.refill(editAndRemove(clientId))
  }

  // Mark the open conversation read (fire-and-forget), like the erb surfaces do.
  const markRead = (conversationId) => {
    fetch(`/team/conversations/${conversationId}/read`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
    }).catch(() => {})
  }

  // Read-truth gate (Temper TF-001): pane VISIBILITY, not the presenter's
  // auto-selection, marks the open conversation read. Desktop (paneVisible
  // always true) behaves exactly as today; phone rail-home entry sends nothing
  // until the customer explicitly opens the conversation.
  useEffect(() => {
    if (!activeConversation?.id || activePage !== null || !paneVisible) return
    markRead(activeConversation.id)
  }, [activeConversation?.id, activePage, paneVisible])

  // D6 — light polling for new messages while a conversation is open and the tab
  // is visible (these surfaces have no live Twilio push). A partial reload of the
  // `team` prop pulls in others' messages, unread counts and previews; mark_read
  // on each poll keeps unread honest — gated on pane visibility (TF-001): while
  // the phone rail is home the reload keeps ticking for data freshness, but no
  // read is written for the hidden conversation. Paused on a background tab,
  // cleared on unmount / conversation change / leaving the conversation view.
  useEffect(() => {
    if (!activeConversation?.id || activePage !== null) return undefined

    const POLL_MS = 12000
    let timer = null

    const tick = () => {
      if (document.visibilityState !== "visible") return
      router.reload({ only: ["team"] })
      if (paneVisible) markRead(activeConversation.id)
    }
    const start = () => { if (!timer) timer = setInterval(tick, POLL_MS) }
    const stop = () => { if (timer) { clearInterval(timer); timer = null } }

    const onVisibility = () => (document.visibilityState === "visible" ? start() : stop())
    document.addEventListener("visibilitychange", onVisibility)
    if (document.visibilityState === "visible") start()

    return () => {
      stop()
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [activeConversation?.id, activePage, paneVisible])

  // --- gated / no-teams states ---
  if (!flags.teamBuild) return <GatedState />
  if (teams.length === 0 || !activeTeam) {
    return (
      <>
        <NoTeamsHero onCreate={() => setCreateOpen(true)} />
        <CreateTeamModal open={createOpen} onOpenChange={setCreateOpen} />
      </>
    )
  }

  const isEmptyTeam = activeTeam.memberCount <= 1 && activeTeam.sharedOrders.length === 0

  // Open (or select) a team DM. A known conversation is visited directly; an
  // unopened one POSTs open_dm, which find-or-creates then redirects with ?c=.
  const openDm = (dm) => {
    if (dm.conversationId) {
      visitTeam(activeTeam.id, dm.conversationId)
    } else {
      router.post(`/team/${activeTeam.id}/dms`, { member_user_id: dm.memberUserId }, {
        headers: { "X-CSRF-Token": csrfToken() },
        preserveScroll: true,
      })
    }
  }

  // Open (or lazily create) the order thread for a shared order. POSTs the
  // find-or-create route, which redirects to ?c=. Idempotent server-side.
  const openOrderThread = (orderId) => {
    router.post(`/team/${activeTeam.id}/orders/${orderId}/thread`, {}, {
      headers: { "X-CSRF-Token": csrfToken() },
      preserveScroll: true,
    })
  }

  // A thread rail row: a known thread opens directly; an unopened one POSTs.
  const openThreadRow = (thread) => {
    if (thread.conversationId) {
      visitTeam(activeTeam.id, thread.conversationId)
    } else {
      openOrderThread(thread.orderId)
    }
  }

  const removeMember = (userId) => {
    router.delete(`/team/${activeTeam.id}/members/${userId}`, {
      headers: { "X-CSRF-Token": csrfToken() },
      preserveScroll: true,
    })
  }

  const unshareOrder = (orderId) => {
    router.delete(`/team/${activeTeam.id}/orders/${orderId}`, {
      headers: { "X-CSRF-Token": csrfToken() },
      preserveScroll: true,
    })
  }

  const deleteTeam = () => {
    if (!window.confirm(`Delete ${activeTeam.name}? This removes the team and its channels for everyone.`)) return
    router.delete(`/team/${activeTeam.id}`, {
      headers: { "X-CSRF-Token": csrfToken() },
      preserveScroll: true,
    })
  }

  const railHandlers = { invite: () => setInviteOpen(true), createTeam: () => setCreateOpen(true) }

  let center
  if (activePage === "overview") {
    center = (
      <OverviewPage
        team={team}
        onRename={() => setRenameOpen(true)}
        onDelete={deleteTeam}
        onMembers={() => selectPage("members")}
        onOrders={() => selectPage("orders")}
      />
    )
  } else if (activePage === "members") {
    center = <MembersPage team={team} onInvite={() => setInviteOpen(true)} onOpenDm={openDm} onRemoveMember={setRemovingMember} />
  } else if (activePage === "orders") {
    center = <SharedOrdersPage team={team} onShare={() => setShareOpen(true)} onUnshare={unshareOrder} onOpenThread={openOrderThread} />
  } else {
    center = (
      <ConversationView
        conversation={activeConversation}
        memberCount={activeTeam.memberCount}
        onSend={send}
        sending={sending}
        empty={isEmptyTeam && activeConversation?.kind === "channel"}
        onInvite={() => setInviteOpen(true)}
        onShare={() => setShareOpen(true)}
        onBack={() => setPaneOpen(false)}
        onMembers={() => selectPage("members")}
        pendingItems={pendingItems}
        onRetry={retry}
        onEdit={handleEdit}
        onDismiss={dismiss}
        composerRef={composerRef}
      />
    )
  }

  return (
    <>
      {/* Shell-aware workspace height: dvh in the ≤860 bands (design rule);
          desktop keeps the live 100vh. At phone width the container clips the
          slide-over pane. */}
      <div className="flex h-[calc(100vh-69px)] min-h-[560px] max-[861px]:h-[calc(100dvh-69px)] max-[721px]:relative max-[721px]:overflow-hidden">
        <WorkspaceRail
          team={team}
          activePage={activePage}
          activeConversationId={activeConversation?.id}
          onSelectPage={selectPage}
          onOpenDm={openDm}
          onOpenThread={openThreadRow}
          onCreateChannel={() => setCreateChannelOpen(true)}
          onRenameChannel={(channel) => setRenameChannel(channel)}
          onInvite={railHandlers}
        />

        {/* Phone slide-over (T7/T8): every selection — conversation or
            management page — slides this pane over the rail, reduced-motion
            safe. Desktop widths carry no transform. Management pages get the
            phone-only crumb bar (design .mob-crumb); conversations carry their
            back control in the pane header. */}
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col bg-card",
            "max-[721px]:absolute max-[721px]:inset-0 max-[721px]:z-[5]",
            "transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
            paneOpen ? "max-[721px]:translate-x-0" : "max-[721px]:translate-x-full"
          )}
        >
          {activePage !== null && (
            <div className="hidden shrink-0 items-center gap-2 border-b border-border bg-card px-3.5 py-[11px] max-[721px]:flex">
              <button
                type="button"
                aria-label="Back to channels"
                onClick={() => setPaneOpen(false)}
                className="-ml-1.5 grid size-9 shrink-0 place-items-center rounded-[10px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ChevronLeft className="size-5" strokeWidth={2.2} />
              </button>
              <h2 className="font-display text-base font-bold tracking-[-0.2px]">{PAGE_TITLES[activePage]}</h2>
            </div>
          )}
          {center}
        </div>

        {activePage === null && <MembersRail members={activeTeam.members} />}
      </div>

      <CreateTeamModal open={createOpen} onOpenChange={setCreateOpen} />
      <RenameTeamModal open={renameOpen} onOpenChange={setRenameOpen} team={activeTeam} />
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} team={activeTeam} />
      <ShareOrderModal open={shareOpen} onOpenChange={setShareOpen} team={activeTeam} />
      <CreateChannelModal open={createChannelOpen} onOpenChange={setCreateChannelOpen} team={activeTeam} />
      <RenameChannelModal
        open={!!renameChannel}
        onOpenChange={(open) => { if (!open) setRenameChannel(null) }}
        team={activeTeam}
        channel={renameChannel}
      />
      <RemoveMemberModal
        member={removingMember}
        onOpenChange={(open) => { if (!open) setRemovingMember(null) }}
        onConfirm={(member) => {
          setRemovingMember(null)
          removeMember(member.userId)
        }}
      />
    </>
  )
}

Team.layout = (page) => (
  <AppShell active="Team" crumb={["Team"]}>
    {page}
  </AppShell>
)
